import type * as models from 'pkg/api/models';
import type { AllowedActionable } from 'pkg/api/models/allowed_actionable';
import type { Country } from 'pkg/api/models/country';
import type { Dateable } from 'pkg/api/models/dateable';
import type { SoftDeletable } from 'pkg/api/models/soft_deletable';
import type { SportType } from 'pkg/api/models/sport';
import type { VideoSource } from 'pkg/api/models/video';

export enum Flags {
	ShowOnboarding = 'showOnboarding',
}

export enum Features {
	Advertisements = 'advertisements',
	AggregatedStatistics = 'aggregated-statistics',
	AIAssistant = 'ai-assistant',
	Assessments = 'assessments',
	Calendar = 'calendar',
	Chat = 'chat',
	CustomLinks = 'custom-links',
	Forms = 'forms',
	Imports = 'imports',
	IndividualDevelopment = 'individual-development',
	Matches = 'matches',
	Payments = 'payments',
	Posts = 'posts',
	ResourceManagement = 'resource-management',
	Scheduling = 'scheduling',
	TrainingLibrary = 'training-library',
	Video = 'video',
	Websites = 'websites',
	SingleSignOn = 'sso',
	DirectorySync = 'directory-sync',
	SplashScreen = 'splash-screen',
	CuratedLibrary = 'curated-library',
	Carpool = 'carpool',
	// Link-only feature: an organization link with this feature shares the
	// owning org's library tags with the target org. Never set on a group's
	// own features.
	ShareTags = 'share-tags',
	// Link-only feature: an organization link with this feature makes the
	// target a member of the owning network organization. Never set on a
	// group's own features.
	Network = 'network',
	PublicAPI = 'public-api',
}

export enum MetaField {
	SplashScreenSettings = 'splash-screen-settings',
	PerformanceReviewTemplate = 'performance-review-template',
}

export interface MetaEntry {
	id?: number;
	key: MetaField;
	value: string;
}

export enum ExportUsersFields {
	FirstName = 'firstName',
	LastName = 'lastName',
	Gender = 'gender',
	Email = 'email',
	PhoneNumber = 'phoneNumber',
	Address = 'address',
	City = 'city',
	Region = 'region',
	PostalCode = 'postalCode',
	Nationality = 'nationality',
	PersonalId = 'personalId',
	DateOfBirth = 'dateOfBirth',
	ConnectedParents = 'connectedParents',
	ConnectedChild = 'connectedChild',
}

export enum PaymentProvider {
	Unset = 'unset',
	Dummy = 'dummy',
	Stripe = 'stripe',
	Adyen = 'adyen',
}

export interface ExportUsersPayload {
	firstName?: boolean;
	lastName?: boolean;
	gender?: boolean;
	email?: boolean;
	phoneNumber?: boolean;
	address?: boolean;
	city?: boolean;
	region?: boolean;
	postalCode?: boolean;
	nationality?: boolean;
	personalId?: boolean;
	dateOfBirth?: boolean;
	connectedParents?: boolean;
	connectedChild?: boolean;
}

export enum GroupStatusTypes {
	Unknown = 'unknown',
	Pending = 'pending',
	Active = 'active',
	Banned = 'banned',
	Inactive = 'inactive',
}

export enum OrganizationType {
	Club = 'club',
	League = 'league',
	Network = 'network',
}

export interface Group extends Dateable, SoftDeletable, AllowedActionable {
	id: number;
	links?: Record<string, string>;
	parentGroupId?: number;
	organizationId: number;
	organizationType?: OrganizationType;
	countryCode?: string;
	sportType?: SportType;
	// An IANA zone, e.g. "Europe/Stockholm", when the group has its own.
	timezone?: string | null;
	externalId: string | number;
	allowInvites: boolean;
	name: string;
	slug: string;
	inviteCode: string;
	meta: MetaEntry[];
	flags: Flags[];
	features: Features[];
	videoSources: VideoSource[];
	seasonStartMonth: number;
	profileImageUrl: string;
	primaryColor: string;
	showRatingResults: boolean;
	order: number;
	birthYearFrom?: number;
	birthYearTo?: number;
	sex?: number;

	// LOK integration fields
	lokOrganizationNumber?: string;
	lokFromTime?: number;
	// Only set on the current organization.
	lokActive?: boolean;

	totalChildGroupCount?: number;
	totalPlayerCount?: number;
	totalAdminCount?: number;
	totalLegalGuardianCount?: number;
	totalPendingMembersCount?: number;

	// Whether 360Player has any content for this organization to browse. Only
	// serialized by the group show endpoint, and viewer-dependent — the free
	// legacy content is matched on the viewer's language.
	has360Content?: boolean;

	identityProviders?: models.identityProvider.IdentityProvider[];
	paymentConfigs: models.groupPaymentConfig.GroupPaymentConfig[];
	groupPaymentConfigId?: number;
	organizationLinks?: models.organizationLink.OrganizationLink[];
	sport?: models.sport.Sport;
	children?: Group[];
	parents?: Group[];
	country?: Country;
	organization?: Group;
	userFields?: models.userFields.UserField[];
	groupSetting?: models.groupSettings.GroupSettings;
	depth?: number;
}

/**
 * This interface is used when getting organizations from the self endpoint
 */
export interface OrganizationData extends Group {
	unreadChatCount: number;
	pendingInvitationCount: number;
	networkMemberCount?: number | null;
}

export interface SplashScreenSettings {
	enabled: boolean;
	imageUrl: string;
}
